import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import connect from 'components/lib/connect';
import { NavMenu } from 'components/common/nav/nav_menu';
import { NavLink } from 'components/common/nav/nav_link';
import { MenuCategory } from 'components/common/nav/menu_category';
import SpinnerTwo from 'scripts/presentation/components/common/spinner_two';

function ReportNavigation({
  activeReport,
  isCollapsible = true,
  isIvrReportingEnabled,
  isReportConfigsLoading,
  onReportSelect,
  reportConfigs,
}) {
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState(null);
  const activeParentReport = getActiveParentReport(activeReport);

  const getNavReportData = function(rawNavData) {
    const processedReportData = rawNavData.reduce((navData, report) => {
      if (!navData[report.categoryId]) {
        navData[report.categoryId] = {
          categoryId: report.categoryId,
          childReports: [],
        };
      }
      const reportDataForNav = _.pick(report, ['title', 'urlSlug']);
      navData[report.categoryId].childReports = [reportDataForNav, ...navData[report.categoryId].childReports];
      return navData;
    }, {});

    const sortedCategoryReportData = _.orderBy(processedReportData, [category => category.categoryId.toLowerCase()]);
    const sortedReportAndCategoryReportData = _.map(sortedCategoryReportData, category => {
      category.childReports = _.orderBy(category.childReports, [report => report.title.toLowerCase()]);
      return category;
    });
    return sortedReportAndCategoryReportData;
  };

  // Filter out the IVR End States config because we do not want to show it in the nav
  // IVR has its own sub-tab nav system on the IVR Summary page
  const urlsToFilterOut = ['ivr-end-states'];
  if (!isIvrReportingEnabled) {
    urlsToFilterOut.push('ivr-summary');
  }

  const filteredReportConfigs = _.filter(reportConfigs, config => !_.includes(urlsToFilterOut, config.urlSlug));
  const navReportData = getNavReportData(filteredReportConfigs);

  useEffect(() => {
    setCurrentSelectedCategory(getActiveCategoryInData());
    function getActiveCategoryInData() {
      if (!navReportData) {
        return null;
      }
      const getActiveReportInData = navReportData.find(category =>
        category.childReports.find(report => report.urlSlug === activeParentReport)
      );
      return getActiveReportInData && getActiveReportInData.categoryId;
    }
  }, [activeParentReport, navReportData]);

  return (
    <NavMenu dataAid={'reporting-nav-menu'} title="Reports">
      {isReportConfigsLoading ? renderLoadingSpinner() : renderPopulatedSideNav()}
    </NavMenu>
  );

  function renderLoadingSpinner() {
    return (
      <SpinnerContainer data-aid="loading-spinner">
        <SpinnerTwo />
      </SpinnerContainer>
    );
  }

  function renderPopulatedSideNav() {
    return navReportData.map(reportingCategory => (
      <MenuCategory
        key={reportingCategory.categoryId}
        dataAid={`menu-category-${reportingCategory.categoryId}`}
        isOpen={!isCollapsible || currentSelectedCategory === reportingCategory.categoryId}
        label={reportingCategory.categoryId}
        onClick={isCollapsible && (() => setCurrentSelectedCategory(reportingCategory.categoryId))}
      >
        {reportingCategory.childReports.map(report => (
          <NavLink
            key={report.title}
            dataAid={`page-link-${report.urlSlug}`}
            isActive={activeParentReport === report.urlSlug}
            onClick={() => onReportSelect(report.urlSlug)}
            to={report.urlSlug}
          >
            {report.title}
          </NavLink>
        ))}
      </MenuCategory>
    ));
  }
}

function getActiveParentReport(activeReport) {
  switch (activeReport) {
    case 'ivr-end-states':
    case 'ivr-summary':
      return 'ivr-summary';
    default:
      return activeReport;
  }
}

ReportNavigation.propTypes = {
  /** Currently selected report */
  activeReport: PropTypes.string,
  /** Boolean to allow collapsing categories */
  isCollapsible: PropTypes.bool,
  /** Boolean to allow IVR Reporting */
  isIvrReportingEnabled: PropTypes.bool,
  /** Boolean to determine report configs loading state */
  isReportConfigsLoading: PropTypes.bool,
  /** Function to call when report is selected */
  onReportSelect: PropTypes.func,
  /** Array of report configs */
  reportConfigs: PropTypes.array,
};

const SpinnerContainer = styled.span`
  align-items: center;
  display: flex;
  flex: 1 1 300px;
  justify-content: center;
  padding-top: ${p => p.theme.spacing.xlarge};
  width: 100%;
`;

function mapStateToProps({ getProvider }) {
  const isReportConfigsLoading = getProvider('reportConfigs').isLoading();

  return {
    isReportConfigsLoading,
  };
}

const ReportNavigationContainer = connect(mapStateToProps)(ReportNavigation);

export default ReportNavigationContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/reporting/report_navigation.jsx